import { useState } from 'react';
import { Banknote, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SettlementBadge } from './SettlementBadge';
import { RegisterPaymentDialog, type PaymentDialogTarget } from './RegisterPaymentDialog';
import { useSettlementApi } from '@/services/api';
import { formatPLN } from '@/utils/currency';
import { t } from '@/i18n';
import type { InvoiceDetail, InvoicePaymentItem } from '@ip/shared-types';

interface InvoicePaymentsCardProps {
  companyNip: string;
  invoice: InvoiceDetail;
  /** Superadmin or company admin/ksiegowy — shows per-payment delete. */
  canManage: boolean;
  /** Reload the invoice so derived settlement fields refresh. */
  onChanged: () => void;
}

function formatDate(date: string | null): string {
  return date ? new Date(date).toLocaleDateString('pl-PL') : '-';
}

/**
 * Task A3: payment history on the sale-invoice detail — date, amount in the
 * invoice currency, PLN equivalent (converted with the invoice's own fxRate),
 * note and author per row; delete only for admins/ksiegowi. The header shows
 * the derived settlement badge + paid/outstanding summary, and the
 * "Zarejestruj wpłatę" button reuses the list's dialog.
 */
export function InvoicePaymentsCard({ companyNip, invoice, canManage, onChanged }: InvoicePaymentsCardProps) {
  const [paymentTarget, setPaymentTarget] = useState<PaymentDialogTarget | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<InvoicePaymentItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { deletePayment } = useSettlementApi();

  const payments = invoice.payments ?? [];
  const currency = invoice.currency || 'PLN';
  const isForeign = Boolean(invoice.currency) && invoice.currency !== 'PLN';

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await deletePayment(companyNip, invoice.id, deleteTarget.id);
      toast.success(t('sales.payments.deleteSuccess'));
      onChanged();
    } catch (error) {
      toast.error(t('sales.payments.deleteError'));
      console.error(error);
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  return (
    <Card className="gap-3 py-4">
      <CardHeader className="px-4">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <Banknote className="size-4" />
          <span>{t('sales.payments.historyTitle')}</span>
          {invoice.settlementStatus && (
            <SettlementBadge
              status={invoice.settlementStatus}
              isOverdue={invoice.isOverdue}
              daysOverdue={invoice.daysOverdue}
            />
          )}
        </CardTitle>
        <CardAction>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setPaymentTarget({
                invoiceId: invoice.id,
                invoiceNumber: invoice.invoiceNumber,
                currency: invoice.currency,
                outstanding: invoice.outstanding,
              })
            }
          >
            <Plus className="size-4" />
            {t('sales.payments.register')}
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="space-y-3 px-4">
        {(invoice.paidAmount !== undefined || invoice.outstanding !== undefined) && (
          <div className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-xs">
            {invoice.paidAmount !== undefined && (
              <span>
                {t('sales.payments.paidLabel')}:{' '}
                <span className="text-foreground font-medium tabular-nums">
                  {formatPLN(invoice.paidAmount)} {currency}
                </span>
                {isForeign && invoice.paidAmountPln !== undefined && (
                  <span> ({t('fx.approxPln', { amount: formatPLN(invoice.paidAmountPln) })})</span>
                )}
              </span>
            )}
            {invoice.outstanding !== undefined && (
              <span>
                {t('sales.payments.outstandingLabel')}:{' '}
                <span className="text-foreground font-medium tabular-nums">
                  {formatPLN(invoice.outstanding)} {currency}
                </span>
              </span>
            )}
          </div>
        )}

        {payments.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('sales.payments.noPayments')}</p>
        ) : (
          <div className="space-y-2">
            {payments.map((payment) => (
              <div
                key={payment.id}
                className="bg-muted/40 flex items-start justify-between gap-2 rounded-md border px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="text-sm">
                    <span className="tabular-nums">{formatDate(payment.paidAt)}</span>
                    {' · '}
                    <span className="font-medium tabular-nums">
                      {formatPLN(payment.amount)} {currency}
                    </span>
                    {isForeign && (
                      <span className="text-muted-foreground text-xs">
                        {' '}
                        ({t('fx.approxPln', { amount: formatPLN(payment.amountPln) })})
                      </span>
                    )}
                  </p>
                  {payment.note && (
                    <p className="text-muted-foreground truncate text-xs" title={payment.note}>
                      {payment.note}
                    </p>
                  )}
                  <p className="text-muted-foreground text-xs">
                    {t('sales.payments.recordedBy', { name: payment.createdByName })}
                  </p>
                </div>
                {canManage && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive size-6 shrink-0"
                    aria-label={t('common.delete')}
                    onClick={() => setDeleteTarget(payment)}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <RegisterPaymentDialog
        companyNip={companyNip}
        target={paymentTarget}
        onOpenChange={(open) => !open && setPaymentTarget(null)}
        onSaved={onChanged}
      />

      <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('sales.payments.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('sales.payments.deleteConfirmMessage')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              disabled={deleting}
              onClick={handleDelete}
            >
              {deleting && <Loader2 className="size-4 animate-spin" />}
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
